import { useEffect, useRef, useState } from "react";

import { useDashboardRealtime } from "../../hooks/useDashboardRealtime";

// ---------------------------------------------------------------------------
// Hook: useInstanceTokenSeries
// Per-instance ring-buffer of token deltas (input + output) sampled once per
// Second — drives the focused InstanceCard sparkline. Pure count-per-sample,
// Not a rate, so a quiet actor reads zero.
// ---------------------------------------------------------------------------

const SAMPLE_INTERVAL_MS = 1500;
const MAX_SAMPLES = 24;

export function useInstanceTokenSeries(instanceId: string | null): number[] {
  const pendingRef = useRef<number>(0);
  const [samples, setSamples] = useState<number[]>([]);
  const { subscribe } = useDashboardRealtime();
  const idRef = useRef(instanceId);
  idRef.current = instanceId;

  useEffect(() => {
    pendingRef.current = 0;
    setSamples([]);
  }, [instanceId]);

  useEffect(() => {
    if (!instanceId) {
      return;
    }
    const unsubscribe = subscribe(
      (message) => {
        if (message.type !== "instance_activity") {
          return;
        }
        const { payload } = message;
        if (
          typeof payload.instance_id !== "string" ||
          payload.instance_id !== idRef.current
        ) {
          return;
        }
        if (payload.activity_type !== "token_delta") {
          return;
        }
        const detail =
          typeof payload.detail === "object" && payload.detail !== null
            ? payload.detail
            : null;
        if (!detail) {
          return;
        }
        const input =
          typeof detail.input_tokens_delta === "number"
            ? detail.input_tokens_delta
            : 0;
        const output =
          typeof detail.output_tokens_delta === "number"
            ? detail.output_tokens_delta
            : 0;
        pendingRef.current += input + output;
      },
      { topic: "internalEventTopic" },
    );
    return unsubscribe;
  }, [instanceId, subscribe]);

  useEffect(() => {
    if (!instanceId) {
      return;
    }
    const id = window.setInterval(() => {
      const sampleValue = pendingRef.current;
      pendingRef.current = 0;
      setSamples((prev) => {
        const next = [...prev, sampleValue];
        return next.length > MAX_SAMPLES ? next.slice(-MAX_SAMPLES) : next;
      });
    }, SAMPLE_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [instanceId]);

  return samples;
}
